import React, { useCallback, useState } from 'react';
import { SquareIcon, Save, RefreshCw, Calculator, Ruler } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import RoomShapeSection from './RoomShapeSection';
import RoomDimensionsSection from './RoomDimensionsSection';
import AdditionalFeaturesSection from './AdditionalFeaturesSection';
import MaterialsSection from './MaterialsSection';

// Импорт утилитарных функций
import { isPositiveNumber } from '../../../../../utils/validation';

/**
 * Основной компонент формы калькулятора площади помещения
 */
const CalculationForm = ({
  initialFormData, 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isCalculating, 
  setIsCalculating,
  results,
  setResults,
  roomShapes,
  measurementUnits,
  flooringTypes,
  wallMaterialTypes,
  ceilingTypes,
  saveRoom 
}) => {
  
  // Состояние для имени сохраняемой комнаты
  const [saveRoomName, setSaveRoomName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Функция парсинга числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const newState = { ...prev, [name]: fieldValue };

      // Для автоматического обновления цен материалов при выборе типа
      if (name === 'flooringType') {
        const selectedType = flooringTypes.find(type => type.id === fieldValue);
        if (selectedType) {
          newState.flooringUnitCost = selectedType.standardPrice.toString();
        }
      }

      if (name === 'wallMaterialType') {
        const selectedType = wallMaterialTypes.find(type => type.id === fieldValue);
        if (selectedType) {
          newState.wallMaterialUnitCost = selectedType.standardPrice.toString();
        }
      }

      if (name === 'ceilingType') {
        const selectedType = ceilingTypes.find(type => type.id === fieldValue);
        if (selectedType) {
          newState.ceilingUnitCost = selectedType.standardPrice.toString();
        }
      }

      // Сброс связанных полей если функция отключена
      if (name === 'includeSkirting' && !checked) {
        newState.skirtingHeight = '0.1';
      }
      
      if (name === 'includeWindows' && !checked) {
        newState.windowCount = '2';
        newState.windowHeight = '1.5';
        newState.windowWidth = '1.2';
      }

      if (name === 'includeDoors' && !checked) {
        newState.doorCount = '1';
        newState.doorHeight = '2.0';
        newState.doorWidth = '0.8';
      }

      if (name === 'includeBalconies' && !checked) {
        newState.balconyLength = '3.0';
        newState.balconyWidth = '1.2';
      }

      return newState;
    });

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [flooringTypes, wallMaterialTypes, ceilingTypes, errors, setErrors, setFormData]);

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const { 
      roomShape, measurementUnit,
      length, width, 
      mainLength, mainWidth, extensionLength, extensionWidth,
      baseLength, baseWidth, cutoutLength, cutoutWidth,
      roomLength, roomWidth, bayWindowDepth, bayWindowWidth,
      roomLengthWithNiche, roomWidthWithNiche, nicheDepth, nicheWidth,
      roomLengthWithColumn, roomWidthWithColumn, columnSide,
      diameter, ceilingHeight,
      windowCount, windowHeight, windowWidth,
      doorCount, doorHeight, doorWidth,
      balconyLength, balconyWidth,
      flooringUnitCost, wallMaterialUnitCost, ceilingUnitCost
    } = formData;

    // Общая валидация для всех типов комнат
    if (!roomShape) newErrors.roomShape = 'Выберите форму комнаты.';
    if (!measurementUnit) newErrors.measurementUnit = 'Выберите единицу измерения.';
    
    // Проверка высоты потолка
    const numCeilingHeight = parseFloatOrDefault(ceilingHeight, -1);
    if (numCeilingHeight <= 0) newErrors.ceilingHeight = 'Введите положительное значение.';
    if (!newErrors.ceilingHeight && numCeilingHeight > 10 && measurementUnit === 'meters') 
      newErrors.ceilingHeight = 'Высота потолка не может быть более 10 м.';
    
    // Валидация в зависимости от формы комнаты
    switch (roomShape) {
      case 'rectangle': {
        const numLength = parseFloatOrDefault(length, -1);
        const numWidth = parseFloatOrDefault(width, -1);
        
        if (numLength <= 0) newErrors.length = 'Введите положительное значение.';
        if (numWidth <= 0) newErrors.width = 'Введите положительное значение.';
        
        // Проверка максимальных размеров для разных единиц измерения
        if (measurementUnit === 'meters') {
          if (!newErrors.length && numLength > 100) newErrors.length = 'Длина не может быть более 100 м.';
          if (!newErrors.width && numWidth > 100) newErrors.width = 'Ширина не может быть более 100 м.';
        } else if (measurementUnit === 'centimeters') {
          if (!newErrors.length && numLength > 10000) newErrors.length = 'Длина не может быть более 10000 см.';
          if (!newErrors.width && numWidth > 10000) newErrors.width = 'Ширина не может быть более 10000 см.';
        } else if (measurementUnit === 'millimeters') {
          if (!newErrors.length && numLength > 100000) newErrors.length = 'Длина не может быть более 100000 мм.';
          if (!newErrors.width && numWidth > 100000) newErrors.width = 'Ширина не может быть более 100000 мм.';
        }
        break;
      }
      
      case 'l-shaped': {
        const numMainLength = parseFloatOrDefault(mainLength, -1);
        const numMainWidth = parseFloatOrDefault(mainWidth, -1);
        const numExtensionLength = parseFloatOrDefault(extensionLength, -1);
        const numExtensionWidth = parseFloatOrDefault(extensionWidth, -1);
        
        if (numMainLength <= 0) newErrors.mainLength = 'Введите положительное значение.';
        if (numMainWidth <= 0) newErrors.mainWidth = 'Введите положительное значение.';
        if (numExtensionLength <= 0) newErrors.extensionLength = 'Введите положительное значение.';
        if (numExtensionWidth <= 0) newErrors.extensionWidth = 'Введите положительное значение.';
        
        // Валидация размеров Г-образной комнаты
        if (measurementUnit === 'meters') {
          if (!newErrors.mainLength && numMainLength > 100) newErrors.mainLength = 'Длина не может быть более 100 м.';
          if (!newErrors.mainWidth && numMainWidth > 100) newErrors.mainWidth = 'Ширина не может быть более 100 м.';
          if (!newErrors.extensionLength && numExtensionLength > 100) newErrors.extensionLength = 'Длина не может быть более 100 м.';
          if (!newErrors.extensionWidth && numExtensionWidth > 100) newErrors.extensionWidth = 'Ширина не может быть более 100 м.';
        }
        break;
      }
      
      case 'angled': {
        const numBaseLength = parseFloatOrDefault(baseLength, -1);
        const numBaseWidth = parseFloatOrDefault(baseWidth, -1);
        const numCutoutLength = parseFloatOrDefault(cutoutLength, -1);
        const numCutoutWidth = parseFloatOrDefault(cutoutWidth, -1);
        
        if (numBaseLength <= 0) newErrors.baseLength = 'Введите положительное значение.';
        if (numBaseWidth <= 0) newErrors.baseWidth = 'Введите положительное значение.';
        if (numCutoutLength <= 0) newErrors.cutoutLength = 'Введите положительное значение.';
        if (numCutoutWidth <= 0) newErrors.cutoutWidth = 'Введите положительное значение.';
        
        // Проверка размеров скоса
        if (!newErrors.cutoutLength && numCutoutLength >= numBaseLength) 
          newErrors.cutoutLength = 'Скос не может быть больше или равен основной длине.';
        if (!newErrors.cutoutWidth && numCutoutWidth >= numBaseWidth) 
          newErrors.cutoutWidth = 'Скос не может быть больше или равен основной ширине.';
        break;
      }
      
      case 'bay-window': {
        const numRoomLength = parseFloatOrDefault(roomLength, -1);
        const numRoomWidth = parseFloatOrDefault(roomWidth, -1);
        const numBayWindowDepth = parseFloatOrDefault(bayWindowDepth, -1);
        const numBayWindowWidth = parseFloatOrDefault(bayWindowWidth, -1);
        
        if (numRoomLength <= 0) newErrors.roomLength = 'Введите положительное значение.';
        if (numRoomWidth <= 0) newErrors.roomWidth = 'Введите положительное значение.';
        if (numBayWindowDepth <= 0) newErrors.bayWindowDepth = 'Введите положительное значение.';
        if (numBayWindowWidth <= 0) newErrors.bayWindowWidth = 'Введите положительное значение.';
        
        // Проверка размеров эркера
        if (!newErrors.bayWindowWidth && numBayWindowWidth > numRoomWidth) 
          newErrors.bayWindowWidth = 'Ширина эркера не может быть больше ширины комнаты.';
        break;
      }
      
      case 'niche': {
        const numRoomLength = parseFloatOrDefault(roomLengthWithNiche, -1);
        const numRoomWidth = parseFloatOrDefault(roomWidthWithNiche, -1);
        const numNicheDepth = parseFloatOrDefault(nicheDepth, -1);
        const numNicheWidth = parseFloatOrDefault(nicheWidth, -1);
        
        if (numRoomLength <= 0) newErrors.roomLengthWithNiche = 'Введите положительное значение.';
        if (numRoomWidth <= 0) newErrors.roomWidthWithNiche = 'Введите положительное значение.';
        if (numNicheDepth <= 0) newErrors.nicheDepth = 'Введите положительное значение.';
        if (numNicheWidth <= 0) newErrors.nicheWidth = 'Введите положительное значение.';
        
        // Проверка размеров ниши
        if (!newErrors.nicheWidth && numNicheWidth > numRoomWidth) 
          newErrors.nicheWidth = 'Ширина ниши не может быть больше ширины комнаты.';
        break;
      }
      
      case 'column': {
        const numRoomLength = parseFloatOrDefault(roomLengthWithColumn, -1);
        const numRoomWidth = parseFloatOrDefault(roomWidthWithColumn, -1);
        const numColumnSide = parseFloatOrDefault(columnSide, -1);
        
        if (numRoomLength <= 0) newErrors.roomLengthWithColumn = 'Введите положительное значение.';
        if (numRoomWidth <= 0) newErrors.roomWidthWithColumn = 'Введите положительное значение.';
        if (numColumnSide <= 0) newErrors.columnSide = 'Введите положительное значение.';
        
        // Проверка размеров колонны
        if (!newErrors.columnSide && numColumnSide > Math.min(numRoomLength, numRoomWidth) / 2) 
          newErrors.columnSide = 'Размер колонны слишком большой для данной комнаты.';
        break;
      }
      
      case 'circle': {
        const numDiameter = parseFloatOrDefault(diameter, -1);
        
        if (numDiameter <= 0) newErrors.diameter = 'Введите положительное значение.';
        
        // Проверка максимального диаметра
        if (measurementUnit === 'meters') {
          if (!newErrors.diameter && numDiameter > 100) newErrors.diameter = 'Диаметр не может быть более 100 м.';
        }
        break;
      }
    }
    
    // Проверка параметров окон, если они включены
    if (formData.includeWindows) {
      const numWindowCount = parseInt(windowCount, 10);
      const numWindowHeight = parseFloatOrDefault(windowHeight, -1);
      const numWindowWidth = parseFloatOrDefault(windowWidth, -1);
      
      if (isNaN(numWindowCount) || numWindowCount <= 0) newErrors.windowCount = 'Введите положительное целое число.';
      if (numWindowHeight <= 0) newErrors.windowHeight = 'Введите положительное значение.';
      if (numWindowWidth <= 0) newErrors.windowWidth = 'Введите положительное значение.';
      
      // Дополнительные проверки размеров окон
      if (measurementUnit === 'meters') {
        if (!newErrors.windowCount && numWindowCount > 20) newErrors.windowCount = 'Количество окон не может быть более 20.';
        if (!newErrors.windowHeight && numWindowHeight > 3) newErrors.windowHeight = 'Высота окна не может быть более 3 м.';
        if (!newErrors.windowWidth && numWindowWidth > 5) newErrors.windowWidth = 'Ширина окна не может быть более 5 м.';
      }
    }
    
    // Проверка параметров дверей, если они включены
    if (formData.includeDoors) {
      const numDoorCount = parseInt(doorCount, 10);
      const numDoorHeight = parseFloatOrDefault(doorHeight, -1);
      const numDoorWidth = parseFloatOrDefault(doorWidth, -1);
      
      if (isNaN(numDoorCount) || numDoorCount <= 0) newErrors.doorCount = 'Введите положительное целое число.';
      if (numDoorHeight <= 0) newErrors.doorHeight = 'Введите положительное значение.';
      if (numDoorWidth <= 0) newErrors.doorWidth = 'Введите положительное значение.';
      
      // Дополнительные проверки размеров дверей
      if (measurementUnit === 'meters') {
        if (!newErrors.doorCount && numDoorCount > 10) newErrors.doorCount = 'Количество дверей не может быть более 10.';
        if (!newErrors.doorHeight && numDoorHeight > 3) newErrors.doorHeight = 'Высота двери не может быть более 3 м.';
        if (!newErrors.doorWidth && numDoorWidth > 2) newErrors.doorWidth = 'Ширина двери не может быть более 2 м.';
      }
    }
    
    // Проверка параметров балкона, если он включен
    if (formData.includeBalconies) {
      const numBalconyLength = parseFloatOrDefault(balconyLength, -1);
      const numBalconyWidth = parseFloatOrDefault(balconyWidth, -1);
      
      if (numBalconyLength <= 0) newErrors.balconyLength = 'Введите положительное значение.';
      if (numBalconyWidth <= 0) newErrors.balconyWidth = 'Введите положительное значение.';
      
      // Дополнительные проверки размеров балкона
      if (measurementUnit === 'meters') {
        if (!newErrors.balconyLength && numBalconyLength > 10) newErrors.balconyLength = 'Длина балкона не может быть более 10 м.';
        if (!newErrors.balconyWidth && numBalconyWidth > 5) newErrors.balconyWidth = 'Ширина балкона не может быть более 5 м.';
      }
    }
    
    // Проверка стоимости материалов
    const numFlooringUnitCost = parseFloatOrDefault(flooringUnitCost, -1);
    const numWallMaterialUnitCost = parseFloatOrDefault(wallMaterialUnitCost, -1);
    const numCeilingUnitCost = parseFloatOrDefault(ceilingUnitCost, -1);
    
    if (numFlooringUnitCost < 0) newErrors.flooringUnitCost = 'Введите неотрицательное значение.';
    if (numWallMaterialUnitCost < 0) newErrors.wallMaterialUnitCost = 'Введите неотрицательное значение.';
    if (numCeilingUnitCost < 0) newErrors.ceilingUnitCost = 'Введите неотрицательное значение.';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, setErrors]);

  // Вычисление площади полигона по координатам вершин
  const calculatePolygonArea = (points) => {
    let area = 0;
    const n = points.length;
    
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n;
      area += points[i].x * points[j].y;
      area -= points[j].x * points[i].y;
    }
    
    area = Math.abs(area) / 2;
    return area;
  };
  
  // Конвертация из выбранных единиц измерения в метры
  const convertToMeters = (value, unit) => {
    switch (unit) {
      case 'centimeters': return value / 100;
      case 'millimeters': return value / 1000;
      default: return value;
    }
  };
  
  // Конвертация из метров в выбранные единицы измерения
  const convertFromMeters = (value, unit) => {
    switch (unit) {
      case 'centimeters': return value * 100;
      case 'millimeters': return value * 1000;
      default: return value;
    }
  };

  // Функция расчета площадей помещения
  const calculateRoomAreas = useCallback(() => {
    if (!validateForm()) {
      // Опционально прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsCalculating(true);
    setResults(null);

    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        const {
          roomShape,
          measurementUnit,
          length, width,
          mainLength, mainWidth, extensionLength, extensionWidth,
          baseLength, baseWidth, cutoutLength, cutoutWidth,
          roomLength, roomWidth, bayWindowDepth, bayWindowWidth,
          roomLengthWithNiche, roomWidthWithNiche, nicheDepth, nicheWidth,
          roomLengthWithColumn, roomWidthWithColumn, columnSide,
          diameter,
          ceilingHeight,
          includeSkirting, skirtingHeight,
          includeWindows, windowCount, windowHeight, windowWidth,
          includeDoors, doorCount, doorHeight, doorWidth,
          includeBalconies, balconyLength, balconyWidth,
          flooringType, flooringUnitCost,
          wallMaterialType, wallMaterialUnitCost,
          ceilingType, ceilingUnitCost
        } = formData;
        
        // Переводим значения в метры для расчета
        let floorAreaSqM = 0;
        let wallPerimeterM = 0;
        let wallAreaSqM = 0;
        let ceilingAreaSqM = 0;
        let balconyAreaSqM = 0;
        
        // Площадь проемов (окон и дверей)
        let openingsAreaSqM = 0;
        
        // Расчет в зависимости от формы комнаты
        switch (roomShape) {
          case 'rectangle': {
            const lengthM = convertToMeters(parseFloatOrDefault(length, 0), measurementUnit);
            const widthM = convertToMeters(parseFloatOrDefault(width, 0), measurementUnit);
            
            floorAreaSqM = lengthM * widthM;
            wallPerimeterM = 2 * (lengthM + widthM);
            break;
          }
          
          case 'l-shaped': {
            const mainLengthM = convertToMeters(parseFloatOrDefault(mainLength, 0), measurementUnit);
            const mainWidthM = convertToMeters(parseFloatOrDefault(mainWidth, 0), measurementUnit);
            const extensionLengthM = convertToMeters(parseFloatOrDefault(extensionLength, 0), measurementUnit);
            const extensionWidthM = convertToMeters(parseFloatOrDefault(extensionWidth, 0), measurementUnit);
            
            // Площадь Г-образной комнаты = площадь основной части + площадь выступа
            floorAreaSqM = (mainLengthM * mainWidthM) + (extensionLengthM * extensionWidthM);
            
            // Периметр Г-образной комнаты
            wallPerimeterM = 2 * (mainLengthM + mainWidthM + extensionLengthM + extensionWidthM) - 2 * Math.min(mainWidthM, extensionWidthM);
            break;
          }
          
          case 'angled': {
            const baseLengthM = convertToMeters(parseFloatOrDefault(baseLength, 0), measurementUnit);
            const baseWidthM = convertToMeters(parseFloatOrDefault(baseWidth, 0), measurementUnit);
            const cutoutLengthM = convertToMeters(parseFloatOrDefault(cutoutLength, 0), measurementUnit);
            const cutoutWidthM = convertToMeters(parseFloatOrDefault(cutoutWidth, 0), measurementUnit);
            
            // Площадь комнаты со скошенным углом = площадь прямоугольника - площадь скоса
            floorAreaSqM = (baseLengthM * baseWidthM) - (cutoutLengthM * cutoutWidthM);
            
            // Периметр комнаты со скошенным углом
            const diagonalLength = Math.sqrt(Math.pow(cutoutLengthM, 2) + Math.pow(cutoutWidthM, 2));
            wallPerimeterM = 2 * (baseLengthM + baseWidthM) - (cutoutLengthM + cutoutWidthM) + diagonalLength;
            break;
          }
          
          case 'bay-window': {
            const roomLengthM = convertToMeters(parseFloatOrDefault(roomLength, 0), measurementUnit);
            const roomWidthM = convertToMeters(parseFloatOrDefault(roomWidth, 0), measurementUnit);
            const bayWindowDepthM = convertToMeters(parseFloatOrDefault(bayWindowDepth, 0), measurementUnit);
            const bayWindowWidthM = convertToMeters(parseFloatOrDefault(bayWindowWidth, 0), measurementUnit);
            
            // Площадь комнаты с эркером = площадь основной комнаты + площадь эркера
            floorAreaSqM = (roomLengthM * roomWidthM) + (bayWindowDepthM * bayWindowWidthM);
            
            // Периметр комнаты с эркером
            wallPerimeterM = 2 * (roomLengthM + roomWidthM) + 2 * bayWindowDepthM + bayWindowWidthM - roomWidthM;
            break;
          }
          
          case 'niche': {
            const roomLengthM = convertToMeters(parseFloatOrDefault(roomLengthWithNiche, 0), measurementUnit);
            const roomWidthM = convertToMeters(parseFloatOrDefault(roomWidthWithNiche, 0), measurementUnit);
            const nicheDepthM = convertToMeters(parseFloatOrDefault(nicheDepth, 0), measurementUnit);
            const nicheWidthM = convertToMeters(parseFloatOrDefault(nicheWidth, 0), measurementUnit);
            
            // Площадь комнаты с нишей = площадь основной комнаты + площадь ниши
            floorAreaSqM = (roomLengthM * roomWidthM) + (nicheDepthM * nicheWidthM);
            
            // Периметр комнаты с нишей
            wallPerimeterM = 2 * (roomLengthM + roomWidthM) + 2 * nicheDepthM + nicheWidthM - roomWidthM;
            break;
          }
          
          case 'column': {
            const roomLengthM = convertToMeters(parseFloatOrDefault(roomLengthWithColumn, 0), measurementUnit);
            const roomWidthM = convertToMeters(parseFloatOrDefault(roomWidthWithColumn, 0), measurementUnit);
            const columnSideM = convertToMeters(parseFloatOrDefault(columnSide, 0), measurementUnit);
            
            // Площадь комнаты с колонной = площадь комнаты - площадь колонны
            floorAreaSqM = (roomLengthM * roomWidthM) - (columnSideM * columnSideM);
            
            // Периметр комнаты с колонной (внешние стены + колонна)
            wallPerimeterM = 2 * (roomLengthM + roomWidthM) + 4 * columnSideM;
            break;
          }
          
          case 'circle': {
            const diameterM = convertToMeters(parseFloatOrDefault(diameter, 0), measurementUnit);
            const radius = diameterM / 2;
            
            // Площадь круглой комнаты
            floorAreaSqM = Math.PI * radius * radius;
            
            // Периметр круглой комнаты
            wallPerimeterM = Math.PI * diameterM;
            break;
          }
          
          case 'polygon': {
            // Конвертируем все координаты в метры
            const pointsInMeters = formData.polygonPoints.map(point => ({
              x: convertToMeters(point.x, measurementUnit),
              y: convertToMeters(point.y, measurementUnit)
            }));
            
            // Рассчитываем площадь многоугольника
            floorAreaSqM = calculatePolygonArea(pointsInMeters);
            
            // Рассчитываем периметр многоугольника
            wallPerimeterM = 0;
            for (let i = 0; i < pointsInMeters.length; i++) {
              const j = (i + 1) % pointsInMeters.length;
              const dx = pointsInMeters[j].x - pointsInMeters[i].x;
              const dy = pointsInMeters[j].y - pointsInMeters[i].y;
              wallPerimeterM += Math.sqrt(dx * dx + dy * dy);
            }
            break;
          }
        }
        
        // Высота потолка в метрах
        const ceilingHeightM = convertToMeters(parseFloatOrDefault(ceilingHeight, 0), measurementUnit);
        
        // Площадь потолка равна площади пола
        ceilingAreaSqM = floorAreaSqM;
        
        // Площадь стен = периметр комнаты * высоту
        wallAreaSqM = wallPerimeterM * ceilingHeightM;
        
        // Рассчитываем площадь балкона, если он включен
        if (includeBalconies) {
          const balconyLengthM = convertToMeters(parseFloatOrDefault(balconyLength, 0), measurementUnit);
          const balconyWidthM = convertToMeters(parseFloatOrDefault(balconyWidth, 0), measurementUnit);
          balconyAreaSqM = balconyLengthM * balconyWidthM;
        }
        
        // Рассчитываем площадь проемов (окна и двери)
        if (includeWindows) {
          const numWindowCount = parseInt(windowCount, 10);
          const windowHeightM = convertToMeters(parseFloatOrDefault(windowHeight, 0), measurementUnit);
          const windowWidthM = convertToMeters(parseFloatOrDefault(windowWidth, 0), measurementUnit);
          
          openingsAreaSqM += numWindowCount * windowHeightM * windowWidthM;
        }
        
        if (includeDoors) {
          const numDoorCount = parseInt(doorCount, 10);
          const doorHeightM = convertToMeters(parseFloatOrDefault(doorHeight, 0), measurementUnit);
          const doorWidthM = convertToMeters(parseFloatOrDefault(doorWidth, 0), measurementUnit);
          
          openingsAreaSqM += numDoorCount * doorHeightM * doorWidthM;
        }
        
        // Вычитаем площадь проемов из площади стен
        wallAreaSqM -= openingsAreaSqM;
        
        // Добавляем площадь плинтуса, если он включен
        let skirtingLengthM = 0;
        if (includeSkirting) {
          skirtingLengthM = wallPerimeterM;
          const skirtingHeightM = convertToMeters(parseFloatOrDefault(skirtingHeight, 0), measurementUnit);
          wallAreaSqM -= skirtingLengthM * skirtingHeightM;
        }
        
        // Расчет материалов
        const numFlooringUnitCost = parseFloatOrDefault(flooringUnitCost, 0);
        const numWallMaterialUnitCost = parseFloatOrDefault(wallMaterialUnitCost, 0);
        const numCeilingUnitCost = parseFloatOrDefault(ceilingUnitCost, 0);
        
        const flooringCost = floorAreaSqM * numFlooringUnitCost;
        const wallMaterialCost = wallAreaSqM * numWallMaterialUnitCost;
        const ceilingMaterialCost = ceilingAreaSqM * numCeilingUnitCost;
        
        const totalMaterialCost = flooringCost + wallMaterialCost + ceilingMaterialCost;
        
        // Формируем результаты расчета
        const calculationResults = {
          floorArea: floorAreaSqM,
          wallPerimeter: wallPerimeterM,
          wallArea: wallAreaSqM,
          ceilingArea: ceilingAreaSqM,
          totalArea: floorAreaSqM + wallAreaSqM + ceilingAreaSqM,
          balconyArea: balconyAreaSqM,
          openingsArea: openingsAreaSqM,
          skirtingLength: skirtingLengthM,
          ceilingHeight: ceilingHeightM,
          flooringCost,
          wallMaterialCost,
          ceilingMaterialCost,
          totalMaterialCost,
          measurementUnit,
          roomShape
        };
        
        setResults(calculationResults);
      } catch (error) {
        console.error("Ошибка расчета:", error);
        setErrors({ general: `Произошла ошибка при расчете: ${error.message}. Пожалуйста, проверьте введенные данные или попробуйте позже.` });
        setResults(null);
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  }, [formData, validateForm, errors, setErrors, setResults, setIsCalculating]);

  // Сброс формы
  const resetForm = () => {
    setFormData(initialFormData);
    setResults(null);
    setErrors({});
    setSaveRoomName('');
    setShowSaveDialog(false);
  };

  // Обработчик сохранения помещения
  const handleSaveRoom = () => {
    if (showSaveDialog) {
      const savedResult = saveRoom(saveRoomName || null);
      if (savedResult) {
        alert(`Помещение "${savedResult.name}" успешно сохранено!`);
      }
      setShowSaveDialog(false);
      setSaveRoomName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    calculateRoomAreas();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <SquareIcon className="w-6 h-6 text-fuchsia-500" />
          Параметры помещения
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Основные параметры - форма и единица измерения */}
        <RoomShapeSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          roomShapes={roomShapes}
          measurementUnits={measurementUnits}
        />

        {/* Размеры помещения в зависимости от формы */}
        <RoomDimensionsSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors}
          measurementUnits={measurementUnits}
        />

        {/* Дополнительные параметры (плинтус, окна, двери) */}
        <AdditionalFeaturesSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors}
          measurementUnits={measurementUnits}
        />

        {/* Материалы для отделки */}
        <MaterialsSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors}
          flooringTypes={flooringTypes}
          wallMaterialTypes={wallMaterialTypes}
          ceilingTypes={ceilingTypes}
        />

        {/* Диалог сохранения помещения */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-fuchsia-50 dark:bg-fuchsia-900/20 border border-fuchsia-100 dark:border-fuchsia-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название помещения"
                value={saveRoomName}
                onChange={(e) => setSaveRoomName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSaveRoom}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<Calculator className="w-5 h-5" />}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Рассчитываем...' : 'Рассчитать площадь'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
          {results && !showSaveDialog && (
            <Button
              type="button"
              onClick={handleSaveRoom}
              icon={<Save className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Сохранить помещение
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default CalculationForm;